import React, { useCallback, useMemo } from "react";
import isHotkey from "is-hotkey";
import { Editable, withReact, useSlate, Slate } from "slate-react";
import {
  Editor,
  Transforms,
  createEditor,
  Element as SlateElement,
  Node,
} from "slate";
import { withHistory } from "slate-history";
import { Button, Icon, Toolbar } from "./slateComponents/Index.jsx";

const HOTKEYS = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
  "mod+`": "code",
};
const LIST_TYPES = ["numbered-list", "bulleted-list"];
const TEXT_ALIGN_TYPES = ["left", "center", "right", "justify"];
const SlateEditor = ({
  prevValue,
  editable,
  customMaxW,
  onChangeData,
  title,
  required,
}) => {
  const renderElement = useCallback((props) => <Element {...props} />, []);
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
  const editor = useMemo(() => withHistory(withReact(createEditor())), []);

  // Convierte el contenido crudo de Slate a JSON
  const getEditorJson = () => {
    const content = editor.children;
    return JSON.stringify(content);
  };

  //Establece el contenido del editor a partir de JSON
  const setEditorContentFromJson = (jsonData) => {
    const parsedContent = JSON.parse(jsonData); // Convierte el JSON en un array de nodos
    Transforms.insertNodes(editor, parsedContent); // Establece el contenido del editor
  };

  const getPlainText = (value) => {
    return value.map((node) => Node.string(node)).join("\n"); // Une los textos con saltos de línea entre nodos
  };

  const handleOnChange = () => {
    {
      const content = editor.children;
      if (getPlainText(content) == "") {
        onChangeData("");
      } else {
        const jsonContent = getEditorJson(); // Convierte el contenido a JSON
        onChangeData(jsonContent); // Envia el JSON al padre
      }
    }
  };

  return (
    <div
      className={`w-full relative ${
        editable ? "border-[1px] border-accent-green rounded-lg" : ""
      }`}
    >
      {title && (
        <span className="font-bold absolute top-[-14px] w-fit left-[15px] bg-white px-2 font-poppins text-dark-green">
          {title} {required && " *"}
        </span>
      )}
      <Slate
        editor={editor}
        initialValue={prevValue ? JSON.parse(prevValue) : initialValue}
        onChange={editable ? (newValue) => handleOnChange() : null}
      >
        {editable && (
          <Toolbar className="!m-0 !p-2">
            <MarkButton format="bold" icon="format_bold" />
            <MarkButton format="italic" icon="format_italic" />
            <MarkButton format="underline" icon="format_underlined" />
            <MarkButton format="code" icon="code" />
            <BlockButton format="heading-one" icon="looks_one" />
            <BlockButton format="heading-two" icon="looks_two" />
            <BlockButton format="block-quote" icon="format_quote" />
            <BlockButton format="numbered-list" icon="format_list_numbered" />
            <BlockButton format="bulleted-list" icon="format_list_bulleted" />
            <BlockButton format="left" icon="format_align_left" />
            <BlockButton format="center" icon="format_align_center" />
            <BlockButton format="right" icon="format_align_right" />
            <BlockButton format="justify" icon="format_align_justify" />
          </Toolbar>
        )}
        <Editable
          className={`!px-4 !resize-none !text-wrap !py-2 ${
            editable ? "!h-[150px]" : ""
          } !overflow-y-auto scrollbar-hide !font-poppins border-none focus:ring-transparent outline-none ${customMaxW}`}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          placeholder="Enter some rich text…"
          spellCheck
          autoFocus={editable} // Solo activa el autofocus si es editable
          readOnly={!editable} // Configura la propiedad readOnly para bloquear edición
          onKeyDown={
            editable
              ? (event) => {
                  for (const hotkey in HOTKEYS) {
                    if (isHotkey(hotkey, event)) {
                      event.preventDefault();
                      const mark = HOTKEYS[hotkey];
                      toggleMark(editor, mark);
                    }
                  }
                }
              : null
          }
        />
      </Slate>
    </div>
  );
};
const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? "align" : "type"
  );
  const isList = LIST_TYPES.includes(format);
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  });
  let newProperties;
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    };
  } else {
    newProperties = {
      type: isActive ? "paragraph" : isList ? "list-item" : format,
    };
  }
  Transforms.setNodes(editor, newProperties);
  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};
const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);
  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};
const isBlockActive = (editor, format, blockType = "type") => {
  const { selection } = editor;
  if (!selection) return false;
  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n[blockType] === format,
    })
  );
  return !!match;
};
const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};
const Element = ({ attributes, children, element }) => {
  const style = { textAlign: element.align };
  switch (element.type) {
    case "block-quote":
      return (
        <blockquote
          className="pl-6 border-l-4 border-gray-300 italic text-gray-600"
          style={style}
          {...attributes}
        >
          {children}
        </blockquote>
      );
    case "bulleted-list":
      return (
        <ul className="list-disc pl-6 space-y-2" style={style} {...attributes}>
          {children}
        </ul>
      );
    case "heading-one":
      return (
        <span
          className="text-2xl font-bold mt-4 mb-2"
          style={style}
          {...attributes}
        >
          {children}
        </span>
      );
    case "heading-two":
      return (
        <span
          className="text-xl font-bold mt-3 mb-2"
          style={style}
          {...attributes}
        >
          {children}
        </span>
      );
    case "list-item":
      return (
        <li className="pl-2" style={style} {...attributes}>
          {children}
        </li>
      );
    case "numbered-list":
      return (
        <ol
          className="list-decimal pl-6 space-y-2"
          style={style}
          {...attributes}
        >
          {children}
        </ol>
      );
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      );
  }
};

const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }
  if (leaf.code) {
    children = <code>{children}</code>;
  }
  if (leaf.italic) {
    children = <em>{children}</em>;
  }
  if (leaf.underline) {
    children = <u>{children}</u>;
  }
  return <span {...attributes}>{children}</span>;
};
const BlockButton = ({ format, icon }) => {
  const editor = useSlate();
  return (
    <Button
      active={isBlockActive(
        editor,
        format,
        TEXT_ALIGN_TYPES.includes(format) ? "align" : "type"
      )}
      onMouseDown={(event) => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};
const MarkButton = ({ format, icon }) => {
  const editor = useSlate();
  return (
    <Button
      active={isMarkActive(editor, format)}
      onMouseDown={(event) => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
    >
      <Icon>{icon}</Icon>
    </Button>
  );
};
const initialValue = [
  {
    type: "paragraph",
    children: [{ text: "" }],
  },
];
export default SlateEditor;
